import React from 'react';
import { Calendar } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import styles from './accommodation-blocking-date-range.module.scss';

export interface IAccommodationBlockingDateRange {
  dateFrom: Date;
  dateTo: Date;
  openDate: boolean;
}

interface IAccommodationBlockingDateRangeProperties {
  value: IAccommodationBlockingDateRange;
  onChange: (value: IAccommodationBlockingDateRange) => void;
}

// Hardcoded Date To used whenever Open Date is checked. Month is 0-indexed, so 11 = December.
// Shared with accommodation-blocking-overview.tsx and accommodation-blocking-form-mapper.ts so
// there's a single definition of the "no end date" sentinel instead of independent copies.
export const OPEN_DATE_TO = new Date(2099, 11, 31);

export function isOpenDate(value: string | Date): boolean {
  const date = typeof value === 'string' ? new Date(value) : value;
  return (
    date.getFullYear() === OPEN_DATE_TO.getFullYear() &&
    date.getMonth() === OPEN_DATE_TO.getMonth() &&
    date.getDate() === OPEN_DATE_TO.getDate()
  );
}

function getTomorrow(): Date {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow;
}

function AccommodationBlockingDateRange({ value, onChange }: IAccommodationBlockingDateRangeProperties) {
  const { dateFrom, dateTo, openDate } = value;

  const handleDateFromChange = (newDateFrom: Date) => {
    onChange({ ...value, dateFrom: newDateFrom });
  };

  const handleDateToChange = (newDateTo: Date) => {
    onChange({ ...value, dateTo: newDateTo });
  };

  const handleOpenDateChange = (checked: boolean) => {
    onChange({
      ...value,
      openDate: checked,
      dateTo: checked ? OPEN_DATE_TO : getTomorrow(),
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.field}>
        <label htmlFor="accommodationBlockingDateFrom">Date From</label>
        <Calendar
          id="accommodationBlockingDateFrom"
          value={dateFrom}
          onChange={(event) => handleDateFromChange(event.value as Date)}
          dateFormat="dd/mm/yy"
          placeholder="dd/mm/yyyy"
          showIcon
        />
      </div>

      {!openDate && (
        <div className={styles.field}>
          <label htmlFor="accommodationBlockingDateTo">Date To</label>
          <Calendar
            id="accommodationBlockingDateTo"
            value={dateTo}
            onChange={(event) => handleDateToChange(event.value as Date)}
            dateFormat="dd/mm/yy"
            placeholder="dd/mm/yyyy"
            showIcon
          />
        </div>
      )}

      <div className={styles.field}>
        <span className={styles.checkboxGroup}>
          <Checkbox
            inputId="accommodationBlockingOpenDate"
            checked={openDate}
            onChange={(event) => handleOpenDateChange(Boolean(event.checked))}
          />
          <label htmlFor="accommodationBlockingOpenDate">Open Date</label>
        </span>
      </div>
    </div>
  );
}

export default AccommodationBlockingDateRange;
